import { GraphQLResult } from "@aws-amplify/api-graphql";
import type {
  OnboardingDismissalInput,
  UpdateUserMutation,
  User,
  UserSettingsInput,
} from "~/api/API";
import {
  updateUserOnboardingDismissals,
  updateUserSettings,
} from "~/api/graphql-custom/mutations";
import {
  type ListUsersLiteQuery,
  listUsersLite,
} from "~/api/graphql-custom/queries";
import { resolveUserDisplayName } from "~/common/userUtils";
import { errorReporter, toError } from "~/error-handling";
import { GraphQLClientWrapper, graphqlClient } from "~/lib/graphql-client";

export class UserService {
  private static instance: UserService;
  private readonly client: GraphQLClientWrapper;

  private constructor(client: GraphQLClientWrapper) {
    this.client = client;
  }

  static getInstance(client?: GraphQLClientWrapper): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService(client || graphqlClient);
    }
    return UserService.instance;
  }

  /**
   * Fetch all users with minimal fields and build a display name lookup map.
   * Keys include both User ID and "cognito:<cognitoUserId>" for direct lookup
   * against AuditLog modifiedBy values.
   */
  async fetchUserDisplayNameMap(): Promise<Map<string, string>> {
    const displayNameMap = new Map<string, string>();
    let nextToken: string | null | undefined = null;

    do {
      const response: GraphQLResult<ListUsersLiteQuery> =
        await this.client.graphql({
          query: listUsersLite,
          variables: {
            limit: 1000,
            nextToken,
          },
        });

      const items = response.data?.listUsers?.items ?? [];

      for (const item of items) {
        if (!item) continue;

        const displayName = resolveUserDisplayName({
          ...item,
        } as User);

        displayNameMap.set(item.id, displayName);

        if (item.cognitoUserId) {
          displayNameMap.set(`cognito:${item.cognitoUserId}`, displayName);
        }
      }

      nextToken = response.data?.listUsers?.nextToken;
    } while (nextToken);

    return displayNameMap;
  }

  async updateOnboardingDismissals(
    userId: string,
    onboardingDismissals: OnboardingDismissalInput[],
  ) {
    let response: GraphQLResult<UpdateUserMutation> | null = null;

    try {
      const result = await this.client.graphql({
        query: updateUserOnboardingDismissals,
        variables: {
          input: {
            id: userId,
            onboardingDismissals,
          },
        },
      });

      if ("data" in result) {
        response = result;
      } else if ("errors" in result) {
        response = result;
      } else {
        throw new Error(
          "Unexpected subscription result when updating onboarding dismissals",
        );
      }

      if (response.errors) {
        const errorMessages = Array.isArray(response.errors)
          ? response.errors
              .map((err) => err?.message ?? JSON.stringify(err))
              .join("; ")
          : JSON.stringify(response.errors);
        throw new Error(
          `Failed to update onboarding dismissals: ${errorMessages}`,
        );
      }
    } catch (error) {
      const errObj = toError(error, "Failed to update onboarding dismissals");
      errorReporter.reportApplicationError(errObj, {
        tags: {
          component: "UserService",
          operation: "update_onboarding_dismissals",
        },
        extra: {
          userId,
          onboardingDismissals,
          responseErrors: response?.errors,
        },
      });
      throw errObj;
    }
  }

  /**
   * Persist the user's portal settings (the User settings modal). `settings`
   * replaces the stored object wholesale, so callers pass every field they
   * want kept — spread the current settings and override the one that changed.
   */
  async updateSettings(userId: string, settings: UserSettingsInput) {
    let response: GraphQLResult<UpdateUserMutation> | null = null;

    try {
      const result = await this.client.graphql({
        query: updateUserSettings,
        variables: {
          input: {
            id: userId,
            settings,
          },
        },
      });

      if ("data" in result || "errors" in result) {
        response = result;
      } else {
        throw new Error(
          "Unexpected subscription result when updating user settings",
        );
      }

      if (response.errors) {
        const errorMessages = Array.isArray(response.errors)
          ? response.errors
              .map((err) => err?.message ?? JSON.stringify(err))
              .join("; ")
          : JSON.stringify(response.errors);
        throw new Error(`Failed to update user settings: ${errorMessages}`);
      }
    } catch (error) {
      const errObj = toError(error, "Failed to update user settings");
      errorReporter.reportApplicationError(errObj, {
        tags: {
          component: "UserService",
          operation: "update_settings",
        },
        extra: {
          userId,
          settings,
          responseErrors: response?.errors,
        },
      });
      throw errObj;
    }
  }
}

export const userService = UserService.getInstance(graphqlClient);
